import { ShieldCheck } from "lucide-react";
import {
  CONDIZIONI_PRO_URL,
  GF_URL,
  NOTE_LEGALI_URL,
  PRIVACY_URL,
  VARIANT,
  VIP_ACQUISTA_URL,
  VIP_LOGIN_URL,
  VIP_URL,
} from "@/constants/links";
import { track } from "@/lib/tracker";

const EXPLORE =
  VARIANT === "vip"
    ? [
        // 2026-10-09: parità con il footer live (link alle pagine SEO dedicate)
        // + nuova pagina WhatsApp.
        { label: "Come funziona", href: "#come-funziona" },
        { label: "Funzionalità avanzate", href: "#funzioni" },
        { label: "Monitoraggio WhatsApp", href: "/funzioni/whatsapp/", testId: "footer-nav-whatsapp" },
        { label: "Demo dashboard", href: "#demo", testId: "footer-nav-demo" },
        { label: "Confronta Standard e VIP", href: "#confronta" },
        { label: "Domande frequenti", href: "#domande-frequenti" },
        { label: "Prezzi", href: "/prezzi/" },
        { label: "Installazione", href: "/installazione/" },
        { label: "Acquista VIP", href: VIP_ACQUISTA_URL, testId: "footer-acquista-link" },
        { label: "Accedi a VIP", href: VIP_LOGIN_URL, testId: "footer-login-link" },
      ]
    : [
        { label: "Come funziona", href: "#come-funziona" },
        { label: "Funzioni", href: "#funzioni" },
        { label: "Confronta Guardian Family e VIP", href: "#confronta" },
        { label: "Domande frequenti", href: "#domande-frequenti" },
        { label: "Pannello Genitore", href: "#pannello" },
        { label: "Accedi", href: "/login", testId: "footer-login-link" },
        {
          label: "Guardian Family VIP (prodotto separato · APK)",
          href: VIP_URL,
          external: true,
          testId: "footer-vip-link",
        },
      ];

const LEGAL =
  VARIANT === "vip"
    ? [
        // 2026-10-09: parità con il footer live — pagine SEO dedicate VIP.
        { label: "Azienda e contatti", href: "/azienda/" },
        { label: "Sicurezza e trasparenza", href: "/sicurezza-e-trasparenza/" },
        { label: "Assistenza", href: "/assistenza/" },
        { label: "Informativa privacy", href: PRIVACY_URL, testId: "footer-privacy-link" },
        { label: "Note Legali", href: NOTE_LEGALI_URL, testId: "footer-legal-link" },
        { label: "Condizioni VIP", href: CONDIZIONI_PRO_URL, testId: "footer-condizioni-link" },
      ]
    : [
        { label: "Informativa privacy", href: PRIVACY_URL, testId: "footer-privacy-link" },
        { label: "Note Legali", href: NOTE_LEGALI_URL, testId: "footer-legal-link" },
        { label: "Condizioni VIP", href: CONDIZIONI_PRO_URL, testId: "footer-condizioni-link" },
      ];

export const Footer = () => (
  <footer className="border-t border-white/10 bg-ink pb-10 pt-16 text-white/70">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col gap-12 border-b border-white/10 pb-12 md:flex-row md:justify-between">
        <div className="max-w-xs">
          <a href="/" aria-label="Guardian Family, pagina iniziale" data-testid="footer-logo" className="inline-flex rounded-2xl bg-white/95 p-2">
            <img
              src="/logo-256.webp"
              alt="Guardian Family"
              width="256"
              height="256"
              className="h-14 w-auto"
              data-testid="footer-logo-image"
            />
          </a>
          <p className="mt-4 text-sm leading-relaxed text-white/60">
            {VARIANT === "vip"
              ? "Guardian Family VIP — prodotto separato (APK), supervisione avanzata con dashboard e account dedicati."
              : "Serenità digitale in famiglia. Protezione trasparente, regole chiare, meno discussioni."}
          </p>
        </div>

        <div className="flex flex-wrap gap-14">
          <div>
            <p className="font-display text-xs font-extrabold uppercase tracking-[0.2em] text-yellow">
              Esplora
            </p>
            <ul className="mt-4 space-y-3">
              {EXPLORE.map((l) => (
                <li key={l.label}>
                  <a
                    data-testid={l.testId || `footer-nav-${l.href.replace("#", "")}`}
                    href={l.href}
                    {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    onClick={() => {
                      if (l.testId === "footer-login-link") track("login_click");
                      else if (l.testId === "footer-acquista-link") track("acquista_click");
                      else if (l.testId === "footer-vip-link") track("vip_link_click");
                    }}
                    className="text-sm font-medium transition-colors hover:text-yellow"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-display text-xs font-extrabold uppercase tracking-[0.2em] text-yellow">
              Informazioni
            </p>
            <ul className="mt-4 space-y-3">
              {LEGAL.map((l) => (
                <li key={l.label}>
                  <a
                    data-testid={l.testId}
                    href={l.href}
                    className="text-sm font-medium transition-colors hover:text-yellow"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/40 sm:flex-row">
        <p>
          © 2026 Guardian Family{VARIANT === "vip" ? " VIP" : ""} ·{" "}
          {VARIANT === "vip" ? "vip.guardianfamily.it" : "guardianfamily.it"}
        </p>
        {VARIANT === "vip" ? (
          <p>
            Prodotto separato con dashboard dedicata · Versione standard:{" "}
            <a href={GF_URL} className="underline underline-offset-2 hover:text-yellow">
              guardianfamily.it
            </a>
          </p>
        ) : (
          <p>Guardian Family · 3,89 € al mese · Google Play · iOS in arrivo</p>
        )}
      </div>
    </div>
  </footer>
);
